import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form } from 'react-bootstrap';
import { userService } from '@/shared/services/userService';

export const MAX_PHOTO_BYTES = 2 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
// A browser may declare no type, or a generic one, for a valid image; the
// server then decides by content.
const UNDECLARED_TYPES = ['', 'application/octet-stream'];

const HELP = 'JPEG, PNG or WebP, up to 2 MB. Only you can see your photo.';
const WRONG_TYPE = 'The photo must be a JPEG, PNG or WebP image.';
const TOO_LARGE = 'The photo must be 2 MB or smaller.';
const UPLOAD_FAILED = 'The photo could not be uploaded. Try again.';
const REMOVE_FAILED = 'The photo could not be removed. Try again.';

/**
 * The signed-in person's own profile photo: choose an image, replace it,
 * remove it. `photo` is { size, uploadedAt } or null and `url` is the object
 * URL of the image to preview (null while none is stored); onChange gets the
 * new photo after the server accepts a change. The server checks the type by
 * content and the size again; the checks here only save a round trip.
 */
function ProfilePhotoUpload({ id, photo, url, onChange, name }) {
    const inputRef = useRef(null);
    const replaceRef = useRef(null);
    const pendingFocus = useRef(null);
    const mounted = useRef(true);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    // The control the person just used may be gone after the change, so focus
    // goes to its replacement rather than to the page.
    useEffect(() => {
        if (pendingFocus.current === 'replace') replaceRef.current?.focus();
        if (pendingFocus.current === 'input') inputRef.current?.focus();
        pendingFocus.current = null;
    }, [photo]);

    const say = (text, isError = false) => {
        if (!mounted.current) return;
        setMessage(text);
        setFailed(isError);
    };

    const handleChoose = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || busy) return;
        if (
            !ACCEPTED_TYPES.includes(file.type) &&
            !UNDECLARED_TYPES.includes(file.type)
        ) {
            say(WRONG_TYPE, true);
            return;
        }
        if (file.size > MAX_PHOTO_BYTES) {
            say(TOO_LARGE, true);
            return;
        }
        setBusy(true);
        say('Uploading your photo');
        try {
            const { photo: saved } = await userService.uploadPhoto(file);
            pendingFocus.current = 'replace';
            onChange(saved);
            say('Your photo was saved.');
        } catch (error) {
            say(error.response?.data?.message || UPLOAD_FAILED, true);
        } finally {
            if (mounted.current) setBusy(false);
        }
    };

    const handleRemove = async () => {
        if (busy) return;
        setBusy(true);
        say('Removing your photo');
        try {
            await userService.removePhoto();
            pendingFocus.current = 'input';
            onChange(null);
            say('Your photo was removed.');
        } catch (error) {
            say(error.response?.data?.message || REMOVE_FAILED, true);
        } finally {
            if (mounted.current) setBusy(false);
        }
    };

    const inputId = `${id}-file`;
    const helpId = `${id}-help`;
    const statusId = `${id}-status`;

    return (
        <div>
            <div className="d-flex flex-wrap align-items-center gap-3">
                {url ? (
                    <img
                        src={url}
                        alt={`Profile photo of ${name || 'you'}`}
                        className="profile-photo-preview rounded-circle"
                    />
                ) : (
                    <div
                        className="profile-photo-preview rounded-circle bg-secondary-subtle d-inline-flex align-items-center justify-content-center text-secondary"
                        aria-hidden="true"
                    >
                        {name?.[0]?.toUpperCase() || ''}
                    </div>
                )}
                <div className="flex-grow-1">
                    <Form.Label
                        htmlFor={inputId}
                        className={photo ? 'd-none' : 'fw-medium'}
                    >
                        Choose a photo
                    </Form.Label>
                    <Form.Control
                        ref={inputRef}
                        id={inputId}
                        type="file"
                        className={photo ? 'd-none' : undefined}
                        accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                        aria-describedby={`${helpId} ${statusId}`}
                        aria-disabled={busy ? 'true' : undefined}
                        onChange={handleChoose}
                    />
                    <Form.Text id={helpId} className="text-muted d-block">
                        {HELP}
                    </Form.Text>
                    {photo && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                            <Button
                                ref={replaceRef}
                                variant="outline-primary"
                                size="sm"
                                aria-disabled={busy ? 'true' : undefined}
                                onClick={() =>
                                    !busy && inputRef.current?.click()
                                }
                            >
                                Replace photo
                            </Button>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                aria-disabled={busy ? 'true' : undefined}
                                onClick={handleRemove}
                            >
                                Remove photo
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            <div
                id={statusId}
                role={failed ? 'alert' : 'status'}
                className={`small mt-2${failed ? ' text-danger' : ''}`}
            >
                {message}
            </div>
        </div>
    );
}

ProfilePhotoUpload.propTypes = {
    id: PropTypes.string.isRequired,
    photo: PropTypes.shape({
        size: PropTypes.number,
        uploadedAt: PropTypes.string,
    }),
    url: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    name: PropTypes.string,
};

export default ProfilePhotoUpload;
